import { PermissionEnum } from '@Application/Security/Roles';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import useSubjectOpener from '@UI/Pages/Beneficiaries/Shared/Hooks/useSubjectOpener';
import SubjectCardFrame from '@UI/Pages/Beneficiaries/Shared/SubjectCard/SubjectCardFrame';
import SubjectCardIdentity from '@UI/Pages/Beneficiaries/Shared/SubjectCard/SubjectCardIdentity';
import SubjectCardMetadata from '@UI/Pages/Beneficiaries/Shared/SubjectCard/SubjectCardMetadata';
import SubjectCardTitle from '@UI/Pages/Beneficiaries/Shared/SubjectCard/SubjectCardTitle';
import BeneficiaryName from '@UI/Pages/Beneficiaries/Shared/BeneficiaryName';
import resolveIdentityTint, { type IdentityTint } from '@Guidelines/Theme/helpers/resolveIdentityTint';
import { theme } from '@Guidelines/Theme/theme';
import resolveFacilityPlacement from '@UI/Pages/Beneficiaries/BeneficiaryList/Helpers/resolveFacilityPlacement';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import { openBeneficiarySidebar } from '@UI/Pages/Beneficiaries/Signals/beneficiarySidebarSignal';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type BeneficiaryListItemView from '@UI/Store/Beneficiaries/Views/BeneficiaryListItemView';
import { css } from '@emotion/react';
import { Pencil } from 'lucide-react';
import * as React from 'react';
import { Button, Tag } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import formatLongDate from '@UI/Helpers/formatLongDate';

const BENEFICIARY_CARD_TRACK_COUNT = 3;
const BENEFICIARY_CARD_ROW_GAP = '0.625rem';

const nameRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
`;

const placementStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 12.5px;
	color: ${theme.colors.inkMuted};
`;

const assignedCountStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

/**
 * Recessed strip for the interview and document counters, set off the card's white surface: same
 * radius as an identity tile, `margin-top` making up the difference to the header row's flex `gap`
 * for a full 1rem of breathing room, matching the sibling `actionsRowStyle` trick below. It carries
 * the card's own identity tint rather than a neutral, so the strip and the tile above it read as
 * one record — the tile takes the tint's `solid`, the strip its `surface`, since its text is dark
 * (`reference/design-direction.md` §4: a tinted surface always pairs with its own ink).
 */
const statsRowStyle = (tint: IdentityTint) => css`
	display: flex;
	align-items: center;
	gap: 1.375rem;
	margin-top: 0.375rem;
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${tint.surface};
`;

const statPairStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

/** Hairline separating the two stat pairs. */
const statDividerStyle = css`
	align-self: stretch;
	width: 1.5px;
	background-color: ${theme.colors.borderStrong};
`;

const statValueStyle = (tint: IdentityTint) => css`
	${theme.typography.metric}
	color: ${tint.ink};
`;

const statLabelStyle = (tint: IdentityTint) => css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${tint.ink};
`;

/** Lifted above the name link's overlay, which would otherwise intercept every click here. */
const actionsRowStyle = css`
	position: relative;
	z-index: 1;
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-top: 0.375rem;
	align-self: flex-end;
`;

type Props = {
	readonly beneficiary: BeneficiaryListItemView;
};

/**
 * One beneficiary record, as a card: initials, full name, an "Archivé" tag when relevant, a facility/organization line, a birth date/address
 * metadata line, the assigned-professional count, an interview/document counter strip, a synthesis
 * link, and an edit trigger (opens `BeneficiarySidebar`, gated by `SUBJECT_WRITE`). Laid out as a
 * three-track subgrid — header (identity tile, name, tag, metadata, assigned count), the
 * interview/document counter strip, and the actions row — so sibling cards on the same grid row settle
 * on the same height, block for block.
 */
const BeneficiaryCard: React.FC<Props> = ({ beneficiary }) => {
	const initials = `${beneficiary.firstName.charAt(0)}${beneficiary.lastName.charAt(0)}`.toUpperCase();
	// Same seed as the `IdentityTile` below, so the strip and the tile always land on the same tint.
	const tint = resolveIdentityTint(beneficiary.id);

	const { organizations } = useCurrentOrganization();
	const { currentFacility } = useStrictCurrentFacility();
	const openSubject = useSubjectOpener();
	const isInCurrentFacility = beneficiary.facilityId === currentFacility.id;
	const placement = React.useMemo(
		() => resolveFacilityPlacement(beneficiary.facilityId, organizations),
		[beneficiary.facilityId, organizations],
	);

	// A plain click on a record of another facility switches facility first; a modified click keeps the
	// native link behaviour (new tab, new window).
	const handleNameLinkClick = React.useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			const hasModifierKey = event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;

			if (isInCurrentFacility || hasModifierKey) {
				return;
			}

			event.preventDefault();
			openSubject(
				buildSubjectPath(SubjectAddress.forBeneficiary(beneficiary.id)),
				beneficiary.facilityId,
			);
		},
		[isInCurrentFacility, openSubject, beneficiary.id, beneficiary.facilityId],
	);

	const handleEditClick = React.useCallback(() => {
		openBeneficiarySidebar(beneficiary.id);
	}, [beneficiary.id]);

	const metadataText = React.useMemo(() => {
		const birthText =
			beneficiary.birthDate !== null
				? `${beneficiary.birthDate.toDisplay()} (${beneficiary.birthDate.ageInYears()} ans)`
				: null;
		const parts = [birthText, beneficiary.birthNameMention, beneficiary.homeAddress].filter(
			(part): part is string => part !== null,
		);

		return parts.length > 0 ? parts.join(' · ') : null;
	}, [beneficiary.birthNameMention, beneficiary.birthDate, beneficiary.homeAddress]);

	const assignedCountText = React.useMemo(() => {
		const count = beneficiary.assignedUserIds.length;
		const professionalWord = count > 1 ? 'référents' : 'référent';
		const assignedWord = count > 1 ? 'affectés' : 'affecté';

		return `${count} ${professionalWord} ${assignedWord}`;
	}, [beneficiary.assignedUserIds]);

	const interviewsLabel = React.useMemo(() => {
		return beneficiary.interviewsCount > 1 ? 'entretiens' : 'entretien';
	}, [beneficiary.interviewsCount]);

	const documentsLabel = React.useMemo(() => {
		return beneficiary.documentsCount > 1 ? 'documents' : 'document';
	}, [beneficiary.documentsCount]);

	return (
		<SubjectCardFrame trackCount={BENEFICIARY_CARD_TRACK_COUNT} rowGap={BENEFICIARY_CARD_ROW_GAP}>
			<SubjectCardIdentity initials={initials} seed={beneficiary.id}>
				<div css={nameRowStyle}>
					<SubjectCardTitle
						to={buildSubjectPath(SubjectAddress.forBeneficiary(beneficiary.id))}
						onClick={handleNameLinkClick}
					>
						<BeneficiaryName firstName={beneficiary.firstName} lastName={beneficiary.lastName} />
					</SubjectCardTitle>
					{beneficiary.isArchived && <Tag value="Archivé" severity="secondary" rounded />}
					<ConditionalLoader
						data={beneficiary.isPurgeApproaching ? beneficiary.purgeScheduledAt : null}
					>
						{(purgeScheduledAt) => (
							<Tag
								value={`Suppression le ${formatLongDate(purgeScheduledAt.toISOString())}`}
								severity="warning"
								rounded
							/>
						)}
					</ConditionalLoader>
				</div>

				<ConditionalLoader data={placement}>
					{(resolvedPlacement) => (
						<p css={placementStyle}>
							{resolvedPlacement.facilityName} · {resolvedPlacement.organizationName}
						</p>
					)}
				</ConditionalLoader>

				<ConditionalLoader data={metadataText}>
					<SubjectCardMetadata>{metadataText}</SubjectCardMetadata>
				</ConditionalLoader>

				<p css={assignedCountStyle}>{assignedCountText}</p>
			</SubjectCardIdentity>
			{/* Document count is pinned to zero: no document exists as a domain concept yet, so this
			figure is accurate. Wire it up to a real counter once documents exist. */}
			<div css={statsRowStyle(tint)}>
				<div css={statPairStyle}>
					<span css={statValueStyle(tint)}>{beneficiary.documentsCount}</span>
					<span css={statLabelStyle(tint)}>{documentsLabel}</span>
				</div>
				<div css={statDividerStyle} />
				<div css={statPairStyle}>
					<span css={statValueStyle(tint)}>{beneficiary.interviewsCount}</span>
					<span css={statLabelStyle(tint)}>{interviewsLabel}</span>
				</div>
			</div>
			<div css={actionsRowStyle}>
				<ConditionalLoader data={!beneficiary.isArchived && isInCurrentFacility}>
					<WithPermission permission={PermissionEnum.SUBJECT_WRITE}>
						{/* The wording sits in `children`, not in `label`: the kit resolves `label` *before*
							`aria-label`, so passing both would drop the record's name from the accessible name
							and leave a list of cards offering N buttons all called "Modifier". */}
						<Button
							type="button"
							outlined
							severity="secondary"
							icon={<Pencil size={15} />}
							aria-label={`Modifier le dossier de ${beneficiary.fullName}`}
							onClick={handleEditClick}
						>
							Modifier
						</Button>
					</WithPermission>
				</ConditionalLoader>
			</div>
		</SubjectCardFrame>
	);
};

export default BeneficiaryCard;
